import { colors } from '@kmns-internal/furikake/tokens'
import { Box, Paper, Stack, Typography } from '@mui/material'
import { handleActivateKey } from 'src/features/monitoringDashboardAlpha/handleActivateKey'
import { toReportStatusGroups } from 'src/features/monitoringDashboardAlpha/selector/toReportStatusGroups'
import {
  DashboardSummary,
  ReportStatusGroupKey,
} from 'src/features/monitoringDashboardAlpha/types'

type CardTone = 'success' | 'error' | 'neutral'

const toneStyle: Record<CardTone, { bgcolor: string; borderColor: string }> = {
  // 実施済みは緑。Figma: 背景=alert success (#edf7ed≒green[0])、枠線=success.main。
  success: { bgcolor: colors.green[0], borderColor: colors.success.main },
  // 未実施（期限切れ）は赤。Figma: 背景=alert error (#fdeded≒red[1])、枠線=error.main。
  error: { bgcolor: colors.red[1], borderColor: colors.error.main },
  // 期間中・開始前・全件数はニュートラル
  neutral: { bgcolor: colors.white, borderColor: colors.divider.main },
}

const SummaryCard = ({
  label,
  count,
  tone,
  onClick,
  recordingCount,
}: {
  label: string
  count: number
  tone: CardTone
  onClick: () => void
  /** 「(N件記録中)」として添える内訳件数（>0 のときだけ表示） */
  recordingCount?: number
}) => {
  const style = toneStyle[tone]
  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleActivateKey(onClick)}
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: style.bgcolor,
        borderColor: style.borderColor,
        cursor: 'pointer',
        '&:hover': { boxShadow: 1 },
        '&:focus-visible': { outline: `2px solid ${colors.primary.main}` },
      }}
    >
      <Stack spacing={0.5}>
        <Typography
          variant="body2"
          noWrap
          sx={{ color: colors.text.secondary }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {count}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            件
          </Typography>
          {recordingCount !== undefined && recordingCount > 0 && (
            <Typography
              variant="caption"
              noWrap
              sx={{ color: colors.text.secondary }}
            >
              ({recordingCount}件記録中)
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

type Props = {
  summary: DashboardSummary
  /** カード選択時。status が null のカード（全件数）は全ステータスを表す */
  onSelectStatus: (status: ReportStatusGroupKey | null) => void
}

/**
 * 全現場合計のサマリーカード（5枚）
 *
 * 実施済み / 期限切れ未実施 / 期間中 / 開始前 / 全件数
 * 「期限切れ未実施」「期間中」には記録中（一時保存あり）の件数を「(N件記録中)」と添える。
 * クリックすると、そのステータスグループで絞り込んだ詳細（L2）へ遷移する。
 *
 * 画面幅に応じて折り返す（狭い幅でも各カードが均等になり崩れない）グリッドで配置する。
 */
export const SummaryCards = ({ summary, onSelectStatus }: Props) => {
  const groups = toReportStatusGroups(summary)
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          // md 以上は 4枚 + 縦線 + 全件数。全件数(合計)を縦線で区切る
          md: 'repeat(4, 1fr) auto 1fr',
        },
      }}
    >
      <SummaryCard
        label="実施済み"
        count={groups.submitted}
        tone="success"
        onClick={() => onSelectStatus('submitted')}
      />
      <SummaryCard
        label="期限切れ未実施"
        count={groups.notImplemented}
        recordingCount={groups.notImplementedRecording}
        tone="error"
        onClick={() => onSelectStatus('notImplemented')}
      />
      <SummaryCard
        label="期間中"
        count={groups.duringPeriod}
        recordingCount={groups.duringPeriodRecording}
        tone="neutral"
        onClick={() => onSelectStatus('duringPeriod')}
      />
      <SummaryCard
        label="開始前"
        count={groups.notStarted}
        tone="neutral"
        onClick={() => onSelectStatus('notStarted')}
      />
      {/* 全件数（合計）を区切る縦線。折り返す狭い幅では非表示にする */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '1px',
          bgcolor: colors.divider.main,
          justifySelf: 'center',
        }}
      />
      <SummaryCard
        label="全件数"
        count={groups.total}
        tone="neutral"
        onClick={() => onSelectStatus(null)}
      />
    </Box>
  )
}
